import React, { useState, useEffect, useRef, useMemo, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';

import { Payment, dueReminders, recurringEntries, turkeyDate } from '../utils/paymentRecurrence';
import PaymentNotifications from './PaymentNotifications';

type FilterType = 'all' | 'credit-card' | 'invoice' | 'aidat' | 'market' | 'other';
type StatusFilter = 'all' | 'paid' | 'unpaid' | 'overdue';

class PaymentsErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Payments error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>⚠️ Ödemeler yüklenirken bir hata oluştu</h3>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer', borderRadius: '6px', border: '1px solid #ccc', background: '#f5f5f5' }}
          >
            🔄 Tekrar Dene
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PaymentsInner: React.FC = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [filterMarket, setFilterMarket] = useState<string>('all');
  const [filterMonth, setFilterMonth] = useState<string>('current-month');
  const [searchQuery, setSearchQuery] = useState('');
  const [todayReminders, setTodayReminders] = useState<Payment[]>([]);
  const [reminderDismissed, setReminderDismissed] = useState(false);
  const [newPayment, setNewPayment] = useState({
    type: 'credit-card' as Payment['type'],
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
    recurring: false,
    marketName: '',
  });

  // Load payments from localStorage on mount
  const paymentsLoadedFromApi = useRef(false);
  const paymentsInitialLoadComplete = useRef(false);

  // Load payments from API (with localStorage fallback)
  useEffect(() => {
    if (!user?.id) return;
    if (paymentsInitialLoadComplete.current) return; // Prevent double load

    const loadPayments = async () => {
      setPaymentsLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token');

        const response = await fetch('/api/user-data', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          paymentsLoadedFromApi.current = true;

          if (data.payments && Array.isArray(data.payments) && data.payments.length > 0) {
            // Ensure each payment has valid required fields
            const validPayments = data.payments.filter((p: any) => 
              p && typeof p.id === 'string' && typeof p.amount === 'number'
            ).map((p: any) => ({
              ...p,
              amount: Number(p.amount) || 0,
              description: p.description || '',
              date: p.date || new Date().toISOString().split('T')[0],
              paid: !!p.paid,
              reminder: !!p.reminder,
              marketName: p.marketName || undefined,
            }));
            setPayments(validPayments);
          }

          console.log('✅ Payments loaded from API:', data.payments?.length || 0, 'items');
          paymentsInitialLoadComplete.current = true;
          setPaymentsLoading(false);
          return;
        }
        throw new Error('API error');
      } catch (error) {
        console.log('⚠️ API unavailable');
        paymentsInitialLoadComplete.current = true;
        setPaymentsLoading(false);
      }
    };

    loadPayments();
  }, [user?.id]);

  // Save payments to API whenever they change
  useEffect(() => {
    if (!user?.id || !paymentsInitialLoadComplete.current || paymentsLoading) return;
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/user-data', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payments })
      }).catch(() => {});
    }
  }, [payments, user?.id]);

  // Auto-generate recurring payments for current month
  const recurringGenerated = useRef(false);
  useEffect(() => {
    if (!paymentsInitialLoadComplete.current || paymentsLoading || recurringGenerated.current) return;
    if (payments.length === 0) return;

    const newEntries = recurringEntries(payments, turkeyDate());

    recurringGenerated.current = true;
    if (newEntries.length > 0) {
      setPayments(prev => [...prev, ...newEntries]);
      console.log(`✅ ${newEntries.length} tekrarlayan ödeme bu aya kopyalandı`);
    }
  }, [payments, paymentsLoading]);

  // Include unpaid overdue payments in the in-app reminder banner as well.
  const checkTodayReminders = useCallback(() => {
    const todayStr = turkeyDate();
    const reminders = dueReminders(payments, todayStr);
    setTodayReminders(reminders);

  }, [payments]);

  // Register Service Worker (for push notifications)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  useEffect(() => {
    checkTodayReminders();
  }, [payments, checkTodayReminders]);

  const handleToggleReminder = (id: string) => {
    setPayments(payments.map(payment =>
      payment.id === id ? { ...payment, reminder: !payment.reminder } : payment
    ));
  };

  const handleAddPayment = () => {
    if (newPayment.amount <= 0 || !newPayment.description.trim()) {
      alert('Lütfen geçerli bir tutar ve açıklama girin.');
      return;
    }

    const payment: Payment = {
      id: Date.now().toString(),
      type: newPayment.type,
      amount: newPayment.amount,
      description: newPayment.description,
      date: newPayment.date,
      paid: false,
      recurring: newPayment.recurring,
      reminder: true,
      marketName: newPayment.marketName || undefined,
    };

    setPayments([...payments, payment]);
    setNewPayment({
      type: 'credit-card',
      amount: 0,
      description: '',
      date: new Date().toISOString().split('T')[0],
      recurring: false,
      marketName: '',
    });
    setShowAddForm(false);
  };

  const handleDeletePayment = (id: string) => {
    setPayments(payments.filter(payment => payment.id !== id));
  };

  const handleEditPayment = (payment: Payment) => {
    setEditingPayment(payment);
    setShowEditForm(true);
  };

  const handleUpdatePayment = () => {
    if (!editingPayment) return;

    const updatedPayments = payments.map(payment => 
      payment.id === editingPayment.id ? editingPayment : payment
    );
    setPayments(updatedPayments);
    setShowEditForm(false);
    setEditingPayment(null);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingPayment(null);
  };

  const handleMarkAsPaid = (id: string) => {
    setPayments(payments.map(payment => 
      payment.id === id ? { ...payment, paid: !payment.paid } : payment
    ));
  };

  const getTypeLabel = (type: Payment['type']) => {
    switch (type) {
      case 'credit-card': return '💳 Kredi Kartı';
      case 'invoice': return '� Fatura';
      case 'aidat': return '� Aidat';      case 'market': return '🏪 Market';      case 'other': return '� Diğer';
      default: return 'Diğer';
    }
  };

  const calculateTotals = () => {
    const todayStr = turkeyDate();
    const currentDate = new Date();

    // Filtered totals based on selected month
    const totalAmount = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const todayAmount = filteredPayments
      .filter(payment => payment.date === todayStr)
      .reduce((sum, payment) => sum + payment.amount, 0);
    const paidAmount = filteredPayments
      .filter(payment => payment.paid)
      .reduce((sum, payment) => sum + payment.amount, 0);
    const unpaidAmount = totalAmount - paidAmount;
    const overdueCount = filteredPayments.filter(p => !p.paid && p.date < todayStr).length;

    // All payments for month summary (not filtered by other criteria)
    const allThisMonthAmount = payments
      .filter(payment => {
        const paymentDate = new Date(payment.date);
        return paymentDate.getMonth() === currentDate.getMonth() && 
               paymentDate.getFullYear() === currentDate.getFullYear();
      })
      .reduce((sum, payment) => sum + payment.amount, 0);

    return { totalAmount, todayAmount, thisMonthAmount: allThisMonthAmount, paidAmount, unpaidAmount, overdueCount };
  };

  const isOverdue = (payment: Payment) => {
    return !payment.paid && payment.date < new Date().toISOString().split('T')[0];
  };

  const getMonthFilter = (filterValue: string) => {
    if (filterValue === 'current-month') {
      const now = new Date();
      return { year: now.getFullYear(), month: now.getMonth() };
    }
    if (filterValue === 'next-month') {
      const now = new Date();
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { year: nextMonth.getFullYear(), month: nextMonth.getMonth() };
    }
    if (filterValue === 'all-months') {
      return null;
    }
    // Handle specific month format YYYY-MM
    if (filterValue.includes('-')) {
      const [yearStr, monthStr] = filterValue.split('-');
      return { year: parseInt(yearStr), month: parseInt(monthStr) - 1 };
    }
    return { year: new Date().getFullYear(), month: new Date().getMonth() };
  };

  const isPaymentInMonth = (paymentDate: Date, monthFilter: ReturnType<typeof getMonthFilter>) => {
    if (monthFilter === null) return true;
    return paymentDate.getFullYear() === monthFilter.year && paymentDate.getMonth() === monthFilter.month;
  };

  const categoryBreakdown = useMemo(() => {
    const breakdown: Record<string, { total: number; paid: number; count: number }> = {};
    const monthFilter = getMonthFilter(filterMonth);

    payments.forEach(p => {
      // Apply month filter for category breakdown
      if (monthFilter !== null) {
        const paymentDate = new Date(p.date);
        if (!isPaymentInMonth(paymentDate, monthFilter)) return;
      }
      if (!breakdown[p.type]) breakdown[p.type] = { total: 0, paid: 0, count: 0 };
      breakdown[p.type].total += p.amount;
      if (p.paid) breakdown[p.type].paid += p.amount;
      breakdown[p.type].count++;
    });
    return breakdown;
  }, [payments, filterMonth]);

  const marketBreakdown = useMemo(() => {
    const breakdown: Record<string, { total: number; paid: number; count: number }> = {};
    const monthFilter = getMonthFilter(filterMonth);

    payments.filter(p => p.type === 'market').forEach(p => {
      // Apply month filter for market breakdown
      if (monthFilter !== null) {
        const paymentDate = new Date(p.date);
        if (!isPaymentInMonth(paymentDate, monthFilter)) return;
      }
      const market = p.marketName || 'Belirtilmemiş';
      if (!breakdown[market]) breakdown[market] = { total: 0, paid: 0, count: 0 };
      breakdown[market].total += p.amount;
      if (p.paid) breakdown[market].paid += p.amount;
      breakdown[market].count++;
    });
    return breakdown;
  }, [payments, filterMonth]);

  const filteredPayments = useMemo(() => {
    const todayStr = turkeyDate();
    const monthFilter = getMonthFilter(filterMonth);

    return payments.filter(p => {
      // Month filter
      if (monthFilter !== null) {
        const paymentDate = new Date(p.date);
        if (!isPaymentInMonth(paymentDate, monthFilter)) return false;
      }

      if (filterType !== 'all' && p.type !== filterType) return false;
      const paymentMarket = p.type === 'market' ? (p.marketName || 'Belirtilmemiş') : null;
      if (filterMarket !== 'all' && paymentMarket !== filterMarket) return false;
      if (statusFilter === 'paid' && !p.paid) return false;
      if (statusFilter === 'unpaid' && p.paid) return false;
      if (statusFilter === 'overdue' && (p.paid || p.date >= todayStr)) return false;
      if (searchQuery && !p.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [payments, filterType, statusFilter, filterMarket, filterMonth, searchQuery]);

  const exportCSV = () => {
    const rows = filteredPayments.map(p => ({
      Açıklama: p.description,
      Tür: getTypeLabel(p.type).replace(/[^\w\sığüşöçİĞÜŞÖÇ]/g, '').trim(),
      MarketAdı: p.marketName || '',
      Tutar: p.amount,
      Tarih: p.date,
      Durum: p.paid ? 'Ödendi' : 'Bekliyor',
      Tekrar: p.recurring ? 'Evet' : 'Hayır',
    }));
    const header = Object.keys(rows[0] || {}).join(';');
    const csv = [header, ...rows.map(r => Object.values(r).join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `odemeler_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = calculateTotals();

  return (
    <div className="payments-container">
      <div className="payments-header">
        <h2>💳 Ödemeler</h2>
        <button 
          className="add-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          + Yeni Ödeme
        </button>
      </div>

      {/* Add Payment Form */}
      {showAddForm && (
        <div className="payment-form">
          <h3>💳 Yeni Ödeme Ekle</h3>
          <div className="form-row">
            <div>
              <label>Ödeme Türü</label>
              <select
                value={newPayment.type}
                onChange={(e) => setNewPayment({ ...newPayment, type: e.target.value as Payment['type'] })}
              >
                <option value="credit-card">💳 Kredi Kartı</option>
                <option value="invoice">📄 Fatura</option>
                <option value="aidat">🏠 Aidat</option>
                <option value="market">🏪 Market</option>
                <option value="other">📌 Diğer</option>
              </select>
            </div>
            {newPayment.type === 'market' && (
              <div>
                <label>Market Adı</label>
                <input
                  type="text"
                  placeholder="Market adı (örn: Migros, Tesco...)"
                  value={newPayment.marketName}
                  onChange={(e) => setNewPayment({ ...newPayment, marketName: e.target.value })}
                />
              </div>
            )}
          </div>
          <div className="form-row">
            <div>
              <label>Tutar (₺)</label>
              <input
                type="number"
                placeholder="0.00"
                value={newPayment.amount || ''}
                onChange={(e) => setNewPayment({ ...newPayment, amount: parseFloat(e.target.value) || 0 })}
                min="0.01"
                step="0.01"
              />
            </div>
            <div>
              <label>Ödeme Tarihi</label>
              <input
                type="date"
                value={newPayment.date}
                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Açıklama</label>
              <input
                type="text"
                placeholder="Ödeme açıklaması..."
                value={newPayment.description}
                onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row-single">
            <label className="recurring-label">
              <input
                type="checkbox"
                checked={newPayment.recurring}
                onChange={(e) => setNewPayment({ ...newPayment, recurring: e.target.checked })}
              />
              🔄 Tekrarlayan ödeme (aylık)
            </label>
          </div>
          <div className="form-actions">
            <button onClick={handleAddPayment} className="confirm-button">✅ Kaydet</button>
            <button onClick={() => setShowAddForm(false)} className="cancel-button">❌ İptal</button>
          </div>
        </div>
      )}

      <PaymentNotifications />

      {/* Summary Cards */}
      <div className="payments-summary">
        <div className="summary-card">
          <h3>💰 Toplam</h3>
          <p className="summary-value">₺{totals.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="summary-card">
          <h3>📅 Bu Ay</h3>
          <p className="summary-value">₺{totals.thisMonthAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className="summary-card paid-card">
          <h3>✅ Ödenen</h3>
          <p className="summary-value">₺{totals.paidAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        <div className={`summary-card ${totals.unpaidAmount > 0 ? 'unpaid-card' : ''}`}>
          <h3>⏳ Bekleyen</h3>
          <p className="summary-value">₺{totals.unpaidAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
        {totals.overdueCount > 0 && (
          <div className="summary-card overdue-card">
            <h3>🚨 Gecikmiş</h3>
            <p className="summary-value">{totals.overdueCount} ödeme</p>
          </div>
        )}
      </div>

      {/* Today's Reminder Banner */}
      {todayReminders.length > 0 && !reminderDismissed && (
        <div className="reminder-banner">
          <div className="reminder-banner-content">
            <span className="reminder-banner-icon">🔔</span>
            <div className="reminder-banner-text">
              <strong>Vadesi gelen veya geciken {todayReminders.length} ödemeniz var!</strong>
              <span>
                {todayReminders.map(p => p.description).join(', ')} — Toplam: ₺{todayReminders.reduce((s, p) => s + p.amount, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <button className="reminder-banner-close" onClick={() => setReminderDismissed(true)}>✕</button>
        </div>
      )}

      {/* Category Breakdown */}
      {payments.length > 0 && (
        <div className="category-breakdown">
          <h3>📊 Kategori Dağılımı</h3>
          <div className="category-bars">
            {Object.entries(categoryBreakdown).map(([type, data]) => {
              const percent = totals.totalAmount > 0 ? (data.total / totals.totalAmount) * 100 : 0;
              const paidPercent = data.total > 0 ? (data.paid / data.total) * 100 : 0;
              return (
                <div key={type} className="category-bar-item">
                  <div className="category-bar-header">
                    <span className="category-bar-label">{getTypeLabel(type as Payment['type'])}</span>
                    <span className="category-bar-amount">
                      ₺{data.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      <span className="category-bar-count">({data.count} adet)</span>
                    </span>
                  </div>
                  <div className="category-bar-track">
                    <div className="category-bar-fill" style={{ width: `${percent}%` }}>
                      <div className="category-bar-paid" style={{ width: `${paidPercent}%` }}></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="category-legend">
            <span className="legend-item"><span className="legend-color total"></span> Toplam</span>
            <span className="legend-item"><span className="legend-color paid"></span> Ödenen</span>
          </div>
        </div>
      )}

      {/* Market Breakdown */}
      {payments.length > 0 && Object.keys(marketBreakdown).length > 0 && (
        <div className="category-breakdown">
          <h3>🏪 Market Dağılımı</h3>
          <div className="category-bars">
            {Object.entries(marketBreakdown)
              .sort((a, b) => b[1].total - a[1].total)
              .map(([market, data]) => {
              const percent = totals.totalAmount > 0 ? (data.total / totals.totalAmount) * 100 : 0;
              const paidPercent = data.total > 0 ? (data.paid / data.total) * 100 : 0;
              return (
                <div key={market} className="category-bar-item">
                  <div className="category-bar-header">
                    <span className="category-bar-label">{market}</span>
                    <span className="category-bar-amount">
                      ₺{data.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      <span className="category-bar-count">({data.count} adet)</span>
                    </span>
                  </div>
                  <div className="category-bar-track">
                    <div className="category-bar-fill" style={{ width: `${percent}%` }}>
                      <div className="category-bar-paid" style={{ width: `${paidPercent}%` }}></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="category-legend">
            <span className="legend-item"><span className="legend-color total"></span> Toplam</span>
            <span className="legend-item"><span className="legend-color paid"></span> Ödenen</span>
          </div>
        </div>
      )}

      {/* Filters */}
      {payments.length > 0 && (
        <div className="payments-filters">
          <div className="filter-group">
            <input
              type="text"
              className="filter-search"
              placeholder="🔍 Ödeme ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="filter-select">
              <option value="current-month">Bu Ay</option>
              <option value="next-month">Gelecek Ay</option>
              <option value="all-months">Tüm Aylar</option>
              <optgroup label="Spesifik Ay Seçin">
                {(() => {
                  const months = new Set<string>();
                  payments.forEach(p => {
                    const date = new Date(p.date);
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    months.add(monthKey);
                  });
                  return Array.from(months)
                    .sort()
                    .reverse()
                    .map(monthKey => {
                      const [year, month] = monthKey.split('-');
                      const monthDate = new Date(parseInt(year), parseInt(month) - 1);
                      const monthName = monthDate.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' });
                      return (
                        <option key={monthKey} value={monthKey}>
                          {monthName}
                        </option>
                      );
                    });
                })()}
              </optgroup>
            </select>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value as FilterType)} className="filter-select">
              <option value="all">Tüm Kategoriler</option>
              <option value="credit-card">💳 Kredi Kartı</option>
              <option value="invoice">📄 Fatura</option>
              <option value="aidat">🏠 Aidat</option>
              <option value="market">🏪 Market</option>
              <option value="other">📌 Diğer</option>
            </select>
            <select value={filterMarket} onChange={(e) => setFilterMarket(e.target.value)} className="filter-select">
              <option value="all">Tüm Market'ler</option>
              {Array.from(new Set(payments.filter(p => p.type === 'market').map(p => p.marketName || 'Belirtilmemiş'))).sort().map(market => (
                <option key={market} value={market}>{market === 'Belirtilmemiş' ? '❌ Belirtilmemiş' : `🏪 ${market}`}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)} className="filter-select">
              <option value="all">Tüm Durumlar</option>
              <option value="paid">✅ Ödendi</option>
              <option value="unpaid">⏳ Bekliyor</option>
              <option value="overdue">🚨 Gecikmiş</option>
            </select>
            <button onClick={exportCSV} className="export-btn" title="CSV İndir">📥 CSV</button>
          </div>
        </div>
      )}

      {/* Edit Payment Form */}
      {showEditForm && editingPayment && (
        <div className="payment-form">
          <h3>✏️ Ödeme Düzenle</h3>
          <div className="form-row">
            <div>
              <label>Ödeme Türü</label>
              <select
                value={editingPayment.type}
                onChange={(e) => setEditingPayment({ ...editingPayment, type: e.target.value as Payment['type'] })}
              >
                <option value="credit-card">💳 Kredi Kartı</option>
                <option value="invoice">📄 Fatura</option>
                <option value="aidat">🏠 Aidat</option>
                <option value="market">🏪 Market</option>
                <option value="other">📌 Diğer</option>
              </select>
            </div>
            {editingPayment.type === 'market' && (
              <div>
                <label>Market Adı</label>
                <input
                  type="text"
                  placeholder="Market adı (örn: Migros, Tesco...)"
                  value={editingPayment.marketName || ''}
                  onChange={(e) => setEditingPayment({ ...editingPayment, marketName: e.target.value })}
                />
              </div>
            )}
          </div>
          <div className="form-row">
            <div>
              <label>Tutar (₺)</label>
              <input
                type="number"
                placeholder="0.00"
                value={editingPayment.amount || ''}
                onChange={(e) => setEditingPayment({ ...editingPayment, amount: parseFloat(e.target.value) || 0 })}
                min="0.01"
                step="0.01"
              />
            </div>
            <div>
              <label>Ödeme Tarihi</label>
              <input
                type="date"
                value={editingPayment.date}
                onChange={(e) => setEditingPayment({ ...editingPayment, date: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Açıklama</label>
              <input
                type="text"
                placeholder="Ödeme açıklaması..."
                value={editingPayment.description}
                onChange={(e) => setEditingPayment({ ...editingPayment, description: e.target.value })}
              />
            </div>
          </div>
          <div className="form-actions">
            <button onClick={handleUpdatePayment} className="confirm-button">✅ Kaydet</button>
            <button onClick={handleCancelEdit} className="cancel-button">❌ İptal</button>
          </div>
        </div>
      )}

      {/* Payments List */}
      {payments.length === 0 ? (
        <div className="empty-payments">
          <div className="empty-payments-icon">💳</div>
          <h3>Henüz Ödeme Kaydınız Bulunmuyor</h3>
          <p>Ödemelerinizi takip etmek için yeni ödeme ekleyin.</p>
          <div className="empty-payments-examples">
            <strong>Örnek ödemeler:</strong><br/>
            💳 Kredi kartı ödemesi - 2.500₺<br/>
            📄 Fatura ödemesi - 1.000₺<br/>
            🏠 Aidat ödemesi - 750₺<br/>
            🏪 Market ödemesi - 350₺<br/>
            📌 Diğer ödemeler - 500₺
          </div>
          <p>Yukarıdaki "Yeni Ödeme" butonuna tıklayarak başlayın!</p>
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="empty-payments">
          <div className="empty-payments-icon">🔍</div>
          <h3>Eşleşen Ödeme Bulunamadı</h3>
          <p>Filtre kriterlerinize uygun ödeme yok. Filtreleri temizlemeyi deneyin.</p>
        </div>
      ) : (
        <div className="payments-list">
          <div className="payments-list-header">
            <h3>📋 Ödeme Listesi ({filteredPayments.length})</h3>
          </div>
          <div className="payment-items">
            {[...filteredPayments]
              .sort((a, b) => {
                // Gecikmiş üstte, sonra ödenmeyenler, sonra ödenenler
                const aOverdue = isOverdue(a) ? 0 : a.paid ? 2 : 1;
                const bOverdue = isOverdue(b) ? 0 : b.paid ? 2 : 1;
                if (aOverdue !== bOverdue) return aOverdue - bOverdue;
                return new Date(a.date).getTime() - new Date(b.date).getTime();
              })
              .map((payment) => (
              <div key={payment.id} className={`payment-item ${payment.paid ? 'paid' : ''} ${isOverdue(payment) ? 'overdue' : ''}`}>
                <div className="payment-info">
                  <div className="payment-type">{getTypeLabel(payment.type)}</div>
                  <div className="payment-amount">₺{payment.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="payment-date">{new Date(payment.date).toLocaleDateString('tr-TR')}</div>
                  {payment.type === 'market' && payment.marketName && <div className="payment-market">🏪 {payment.marketName}</div>}
                  {payment.recurring && <div className="recurring-badge">🔄</div>}
                  {payment.paid && <div className="paid-badge">✅ Ödendi</div>}
                  {isOverdue(payment) && <div className="overdue-badge">🚨 Gecikmiş</div>}
                </div>
                <div className="payment-description">{payment.description}</div>
                <div className="payment-actions">
                  <button
                    onClick={() => handleToggleReminder(payment.id)}
                    className={`action-icon-btn alert-btn ${payment.reminder ? 'active' : ''}`}
                    title={payment.reminder ? 'Hatırlatıcı aktif' : 'Hatırlatıcı ekle'}
                  >
                    {payment.reminder ? '🔔' : '🔕'}
                  </button>
                  <button 
                    onClick={() => handleMarkAsPaid(payment.id)}
                    className={`action-icon-btn paid-btn ${payment.paid ? 'active' : ''}`}
                    title={payment.paid ? 'Ödeme iptal' : 'Ödendi olarak işaretle'}
                  >
                    {payment.paid ? '↩️' : '✅'}
                  </button>
                  <button 
                    onClick={() => handleEditPayment(payment)}
                    className="action-icon-btn edit-btn"
                    title="Düzenle"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => handleDeletePayment(payment.id)}
                    className="action-icon-btn delete-btn"
                    title="Sil"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Payments: React.FC = () => (
  <PaymentsErrorBoundary>
    <PaymentsInner />
  </PaymentsErrorBoundary>
);

export default Payments;
